import { useContext, useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { FirebaseError } from '@firebase/util';
import { db } from '../api/firebase/config';
import { AuthContext } from '../contexts/auth';
import { useCompanyId } from './useCompanyId';
import { isApiAccessActive, toApiStartDate } from '../utils/apiFeature';
import { isCompanyAdmin, isOperatorStaff } from '../utils/permission';

/**
 * 右上アカウントメニュー / 各ページに「APIキー管理」導線を出すかどうかを判定する。
 *
 * - companyAdmin / 代理モード: `api_enabled = true` かつ利用開始日に到達している場合に true
 * - 運営スタッフ自身のセッション: `api_enabled = true` であれば事前準備のため常に true
 * - その他のロールでは常に false
 */
export const useApiMenuVisibility = (): boolean => {
  const { user } = useContext(AuthContext);
  const { companyId: effectiveCompanyId, isProxyMode } = useCompanyId();
  const [apiEnabled, setApiEnabled] = useState(false);
  const [apiStartDate, setApiStartDate] = useState<Date | null>(null);

  useEffect(() => {
    const companyIdToUse = effectiveCompanyId ?? user?.company_id;
    if (!companyIdToUse) {
      setApiEnabled(false);
      setApiStartDate(null);
      return;
    }
    const ref = doc(db, 'companies', companyIdToUse);
    const unsubscribe = onSnapshot(
      ref,
      snapshot => {
        if (!snapshot.exists()) {
          setApiEnabled(false);
          setApiStartDate(null);
          return;
        }
        const data = snapshot.data();
        setApiEnabled(data.api_enabled === true);
        setApiStartDate(toApiStartDate(data.api_start_date));
      },
      error => {
        // ログアウト直後の permission-denied は正常動作
        if (error instanceof FirebaseError && error.code === 'permission-denied') {
          return;
        }
      },
    );
    return (): void => unsubscribe();
  }, [effectiveCompanyId, user?.company_id]);

  if (!user || !apiEnabled) return false;
  const isCustomerView = isCompanyAdmin(user.role) || isProxyMode;
  if (isCustomerView) {
    return isApiAccessActive(apiEnabled, apiStartDate);
  }
  return isOperatorStaff(user.role);
};
